import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { T, inputStyle } from "../theme";
import Avatar from "../components/Avatar";
import LangBadge from "../components/LangBadge";

export default function NewChatScreen({
  currentUser,
  onStartConversation,
  onBack,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searching, setSearching] = useState(false);
  const [groupName, setGroupName] = useState("");

  // Search Firestore users by username prefix
  useEffect(() => {
    if (searchQuery.length < 1) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const q = query(
          collection(db, "users"),
          where("username", ">=", searchQuery.toLowerCase()),
          where("username", "<=", searchQuery.toLowerCase() + "\uf8ff"),
          orderBy("username"),
          limit(20)
        );
        const snap = await getDocs(q);
        const users = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((u) => u.uid !== currentUser.uid);
        setResults(users);
      } catch (err) {
        console.error("User search error:", err);
        setResults([]);
      }
      setSearching(false);
    }, 300); // debounce

    return () => clearTimeout(timer);
  }, [searchQuery, currentUser.uid]);

  const toggle = (u) => {
    setSelected((prev) =>
      prev.find((s) => s.uid === u.uid)
        ? prev.filter((s) => s.uid !== u.uid)
        : [...prev, u]
    );
  };

  const handleStart = async () => {
    if (selected.length === 0) return;

    // Build participant arrays including current user
    const participantUids = [
      currentUser.uid,
      ...selected.map((u) => u.uid),
    ];
    const participantLanguages = {
      [currentUser.uid]: currentUser.preferredLanguage,
    };
    selected.forEach((u) => {
      participantLanguages[u.uid] = u.preferredLanguage || "en";
    });

    const isGroup = selected.length >= 2;
    const cleanName = groupName.trim();
    await onStartConversation(participantUids, participantLanguages, {
      groupName: isGroup && cleanName.length > 0 ? cleanName : null,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Header + search */}
      <div
        style={{
          padding: "calc(16px + env(safe-area-inset-top, 0px)) 20px 12px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: T.orange,
              fontSize: 22,
              padding: 0,
            }}
          >
            ←
          </button>
          <span style={{ fontWeight: 700, fontSize: 17 }}>
            New Conversation
          </span>
        </div>
        <input
          placeholder="Search username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoCapitalize="none"
          autoFocus
          style={{ ...inputStyle, fontSize: 14 }}
        />
        {/* Selected chips */}
        {selected.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              flexWrap: "wrap",
            }}
          >
            {selected.map((u) => (
              <div
                key={u.uid}
                onClick={() => toggle(u)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: T.bg3,
                  borderRadius: 20,
                  padding: "4px 10px 4px 4px",
                  cursor: "pointer",
                }}
              >
                <Avatar user={u} size={24} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {(u.displayName || u.username).split(" ")[0]}
                </span>
                <span style={{ color: T.textDim, fontSize: 14 }}>×</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {searching && (
          <div
            style={{
              textAlign: "center",
              color: T.textDim,
              padding: 20,
              fontSize: 13,
            }}
          >
            Searching...
          </div>
        )}
        {!searching && searchQuery.length > 0 && results.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: T.textMute,
              padding: 40,
              fontSize: 14,
            }}
          >
            No users found for "{searchQuery}"
          </div>
        )}
        {results.map((u) => {
          const sel = selected.find((s) => s.uid === u.uid);
          return (
            <div
              key={u.uid}
              onClick={() => toggle(u)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 20px",
                borderBottom: `1px solid ${T.border}`,
                cursor: "pointer",
                background: sel ? T.bg2 : T.bg0,
                transition: "background 0.15s",
              }}
            >
              <Avatar user={u} size={46} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {u.displayName}
                </div>
                <div
                  style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}
                >
                  @{u.username} ·{" "}
                  <LangBadge lang={u.preferredLanguage || "en"} />
                </div>
              </div>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: `2px solid ${sel ? T.orange : T.border}`,
                  background: sel ? T.orange : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 14,
                }}
              >
                {sel ? "✓" : ""}
              </div>
            </div>
          );
        })}
        {!searching && searchQuery.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: T.textMute,
              padding: 40,
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            Type a username to search
          </div>
        )}
      </div>

      {/* Group name (only when 2+ others selected) + Start button */}
      {selected.length > 0 && (
        <div
          style={{
            padding: "16px 20px",
            borderTop: `1px solid ${T.border}`,
            background: T.bg1,
          }}
        >
          {selected.length >= 2 && (
            <input
              placeholder="Group name (optional)"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={60}
              style={{ ...inputStyle, fontSize: 14, marginBottom: 12 }}
            />
          )}
          <button
            onClick={handleStart}
            style={{
              width: "100%",
              padding: "16px 0",
              background: T.orange,
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontWeight: 900,
              fontSize: 15,
              letterSpacing: 0.5,
            }}
          >
            START CHAT{" "}
            {selected.length > 1
              ? `(${selected.length + 1} people)`
              : `with ${(selected[0].displayName || "").split(" ")[0]}`}
          </button>
        </div>
      )}
    </div>
  );
}
